import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type RaceType = 'trail' | 'route' | 'ultra' | 'urbain';

export interface CalendarRace {
  id: string;
  title: string;
  date: Date;
  city: string | null;
  source: 'kourse' | 'external';
  slug?: string | null;
  url?: string | null;
  distances: number[];
  terrain?: 'road' | 'trail' | 'mixed' | 'track' | null;
  type: RaceType;
  km: number;
  image_url?: string | null;
}

export const RACE_TYPES: Record<RaceType, { label: string; color: string; tint: string }> = {
  trail:  { label: 'TRAIL',  color: '#caff00', tint: 'rgba(202,255,0,0.15)' },
  route:  { label: 'ROUTE',  color: '#00e5ff', tint: 'rgba(0,229,255,0.18)' },
  ultra:  { label: 'ULTRA',  color: '#ff2d9e', tint: 'rgba(255,45,158,0.18)' },
  urbain: { label: 'URBAIN', color: '#f8ff3a', tint: 'rgba(248,255,58,0.18)' },
};

export function deriveRaceType(
  distances: number[],
  terrain: CalendarRace['terrain'],
  title?: string | null,
): RaceType {
  const maxKm = distances.length ? Math.max(...distances) : 0;
  if (maxKm >= 50) return 'ultra';
  if (terrain === 'trail') return 'trail';
  if (!terrain && title && /\btrail/i.test(title)) return 'trail';
  if (terrain === 'mixed' || terrain === 'track') return 'urbain';
  return 'route';
}

export function useCalendarRaces() {
  return useQuery({
    queryKey: ['calendar-races'],
    queryFn: async (): Promise<CalendarRace[]> => {
      const today = new Date().toISOString();
      const [eventsRes, externalRes] = await Promise.all([
        supabase
          .from('events')
          .select('id, slug, title, event_date, city, distance_km, terrain, image_url, races:event_races(distance_km)')
          .eq('status', 'published')
          .gte('event_date', today)
          .order('event_date'),
        supabase
          .from('external_events' as any)
          .select('id, name, event_date, city, distances_km, website_url, registration_url')
          .eq('status', 'published')
          .gte('event_date', today)
          .order('event_date'),
      ]);

      const events = (eventsRes.data ?? []).map((e: any) => {
        const raceDistances = (e.races ?? [])
          .map((r: any) => Number(r.distance_km))
          .filter((d: number) => Number.isFinite(d) && d > 0);
        const distances = raceDistances.length
          ? raceDistances
          : e.distance_km
            ? [Number(e.distance_km)]
            : [];
        return {
          id: `k-${e.id}`,
          title: e.title,
          date: new Date(e.event_date),
          city: e.city,
          source: 'kourse' as const,
          slug: e.slug,
          distances,
          terrain: e.terrain,
          type: deriveRaceType(distances, e.terrain, e.title),
          km: distances.length ? Math.max(...distances) : 0,
          image_url: e.image_url ?? null,
        };
      });
      const externals = (externalRes.data ?? []).map((e: any) => {
        const distances: number[] = e.distances_km ?? [];
        return {
          id: `x-${e.id}`,
          title: e.name,
          date: new Date(e.event_date),
          city: e.city,
          source: 'external' as const,
          url: e.registration_url ?? e.website_url ?? null,
          distances,
          terrain: null,
          type: deriveRaceType(distances, null, e.name),
          km: distances.length ? Math.max(...distances) : 0,
          image_url: null,
        };
      });
      return [...events, ...externals].sort((a, b) => a.date.getTime() - b.date.getTime());
    },
  });
}